import {
  ThumbsUp,
  ThumbsDown,
  RotateCcw,
  Trophy,
  Target,
  BarChart2,
} from 'lucide-react';
import { QuizPlayerEndProps } from '../types';
import { useTranslation } from '@/hooks/use-translation';
import { Button as BaseButton, ButtonProps } from '@/components/button';
import { QuizPlayerScreen } from './quiz-player-screen';
import { cn } from '@/utils';

type QuizPlayerEndScreenProps = React.ComponentProps<'div'> &
  QuizPlayerEndProps & {
    slots?: { Button?: React.ElementType<ButtonProps> };
    slotProps?: { button?: ButtonProps };
  };

export function QuizPlayerEndScreen({
  score = 0,
  totalQuestions = 0,
  stats = {},
  onRestart,
  onFeedback = () => {},
  slots = {},
  slotProps,
}: QuizPlayerEndScreenProps) {
  const { t } = useTranslation();
  const { Button = BaseButton } = slots;

  return (
    <QuizPlayerScreen variant="end">
      <div className="mm:text-center mm:mb-8">
        <Trophy size={48} className="mm:mx-auto mm:mb-4 mm:text-yellow-500" />
        <h2 className="mm:text-3xl mm:font-bold mm:text-gray-900 mm:mb-2">
          {t('quizEnd', 'Quiz completato!')}
        </h2>
        <p className="mm:text-muted-foreground">
          {t('quizEndDescription', 'Ecco come sei andato')}
        </p>
      </div>

      <div className="mm:grid mm:grid-cols-1 mm:md:grid-cols-3 mm:gap-6 mm:mb-8">
        <div className="mm:bg-muted mm:p-4 mm:rounded-lg mm:text-center">
          <Trophy size={24} className="mm:mx-auto mm:mb-2 mm:text-foreground" />
          <div className="mm:text-2xl mm:font-bold mm:text-foreground">
            {score} / {totalQuestions}
          </div>
          <div className="mm:text-sm mm:text-foreground">
            {t('currentScore', 'Punteggio ottenuto')}
          </div>
        </div>

        <div className="mm:border-2 mm:border-muted mm:p-4 mm:rounded-lg mm:text-center">
          <Target
            size={24}
            className="mm:mx-auto mm:mb-2 mm:text-muted-foreground"
          />
          <div className="mm:text-2xl mm:font-bold mm:text-muted-foreground">
            {stats.totalAttempts || 0}
          </div>
          <div className="mm:text-sm mm:text-muted-foreground">
            {t('quizAttempts', 'Tentativi')}
          </div>
        </div>

        <div className="mm:border-2 mm:border-muted mm:p-4 mm:rounded-lg mm:text-center">
          <BarChart2
            size={24}
            className="mm:mx-auto mm:mb-2 mm:text-muted-foreground"
          />
          <div className="mm:text-2xl mm:font-bold mm:text-muted-foreground">
            {stats.avgScore || 0} / {totalQuestions}
          </div>
          <div className="mm:text-sm mm:text-muted-foreground">
            {t('avgScore', 'Punteggio medio')}
          </div>
        </div>
      </div>

      <div className="mm:space-y-4 mm:max-w-xs mm:mx-auto">
        <Button
          {...slotProps?.button}
          size="lg"
          variant="brand"
          startIcon={<RotateCcw />}
          className={cn(
            'mm:w-full mm:shadow-none',
            slotProps?.button?.className
          )}
          onClick={onRestart}
        >
          {t('quizRestart', 'Riprova')}
        </Button>

        <div className="mm:flex mm:gap-4">
          <Button
            {...slotProps?.button}
            startIcon={<ThumbsUp />}
            className={cn(
              'mm:flex-1 mm:bg-success/10 mm:text-success mm:hover:bg-success/20 mm:shadow-none',
              slotProps?.button?.className
            )}
            onClick={() => onFeedback(1)}
          >
            {t('helpful', 'Utile')}
          </Button>
          <Button
            {...slotProps?.button}
            startIcon={<ThumbsDown size={20} />}
            className={cn(
              'mm:flex-1 mm:bg-destructive/10 mm:text-destructive mm:hover:bg-destructive/20 mm:shadow-none',
              slotProps?.button?.className
            )}
            onClick={() => onFeedback(-1)}
          >
            {t('notHelpful', 'Non utile')}
          </Button>
        </div>
      </div>
    </QuizPlayerScreen>
  );
}

QuizPlayerEndScreen.displayName = 'QuizPlayerEndScreen';
